/**
 * My ArtRouting menu block: the role switch and a compact Activity summary
 * for the selected role (last 7 days, plus followers and saves right now),
 * with a link to the private Insights report.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { T } from '@/components/T';
import { useActiveRoleStore, type AccountRole } from '@/stores/activeRoleStore';

interface Summary { views: number; followers: number; saves: number; routeAdds: number; hasRecords: boolean }

export default function MenuActivity({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  const { roles, active, load, setActive } = useActiveRoleStore();
  const [s, setS] = useState<Summary | null>(null);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let live = true;
    api.get<Summary>(`/insights/summary?role=${active}`).then((d) => { if (live) setS(d); }).catch(() => { if (live) setS(null); });
    return () => { live = false; };
  }, [active]);

  const figure = (n: number, key: string) => (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, flex: 1 }}>
      <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>{n}</strong>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{t(key)}</span>
    </span>
  );

  return (
    <div className="menu-activity" style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', margin: '4px 0' }}
      onClick={(e) => e.stopPropagation()}>
      {roles.length > 1 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
          <T k="insights.roleLabel" />
          <select value={active} onChange={(e) => setActive(e.target.value as AccountRole)} aria-label={t('insights.roleLabel')}
            style={{ flex: 1, minWidth: 0, fontSize: 12, padding: '2px 4px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6 }}>
            {roles.map((r) => <option key={r} value={r}>{t(`account.role.${r}`)}</option>)}
          </select>
        </label>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}><T k="insights.menuTitle" /></div>
      {s && s.hasRecords ? (
        <div style={{ display: 'flex', gap: 6 }} aria-label={t('insights.menuTitle')}>
          {figure(s.views, 'insights.m.views7')}
          {figure(s.followers, 'insights.m.followers')}
          {figure(s.saves, 'insights.m.saves')}
          {figure(s.routeAdds, 'insights.m.routeAdds7')}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}><T k="insights.menuEmpty" /></div>
      )}
      <Link to="/insights" onClick={onNavigate} style={{ display: 'inline-block', marginTop: 6, fontSize: 12 }}>
        <i className="fas fa-chart-line" style={{ marginRight: 4 }}></i><T k="insights.viewReport" /> &rarr;
      </Link>
    </div>
  );
}
